import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import { Card } from "../components/ui/Card";
import Input from "../components/ui/Input";
import ListingCard, { ListingCardSkeleton } from "../components/ListingCard";
import { pageSeo, useSeo } from "../lib/seo";

const SORT_OPTIONS = [
  { value: "rating", label: "Recommended" },
  { value: "promoted", label: "Most reviewed" },
  { value: "distance", label: "Distance" },
];

const POPULAR_TREATMENTS = [
  "Haircut",
  "Hair coloring",
  "Balayage",
  "Manicure",
  "Pedicure",
  "Eyelash extensions",
  "Eyebrow styling",
  "Facial",
  "Massage",
  "Barber shave",
];

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [promotedOnly, setPromotedOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  // "where" is the old name of the navbar's location param; old links still use it.
  const city = (params.get("city") || params.get("where") || "").trim();
  const sort = params.get("sort") || "rating";
  const date = params.get("date") || "";

  // The search already counted, so sorting or filtering it isn't counted again.
  const countedRef = useRef("");

  useEffect(() => {
    const isNewSearch = Boolean(q) && countedRef.current !== q;
    countedRef.current = q;

    setLoading(true);
    api
      .get("/businesses", {
        params: {
          q: q || undefined,
          category: category || undefined,
          city: city || undefined,
          date: date || undefined,
          sort,
          per_page: 24,
          // A search the customer made, as opposed to suggestions while typing.
          track: isNewSearch ? 1 : undefined,
        },
      })
      .then(({ data }) => {
        setResults(data.data || []);
        setTotal(data.total ?? (data.data || []).length);
      })
      .catch(() => {
        setResults([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [q, category, city, sort, date]);

  const filtered = useMemo(() => {
    return results.filter((b) => {
      if (promotedOnly && !b.is_promoted) return false;
      if (minRating && Number(b.rating_avg || 0) < minRating) return false;
      return true;
    });
  }, [results, promotedOnly, minRating]);

  const setSort = (value) => {
    const next = new URLSearchParams(params);
    next.set("sort", value);
    setParams(next);
  };

  const title = category
    ? `${category}${city ? ` – salons near ${city}` : " – salons near you"}`
    : city
    ? `Salons near ${city}`
    : q
    ? `Results for “${q}”`
    : "Search results";

  // Searches are a tool, not pages for search engines to list: the service
  // and city pages (ServiceLanding) are, and only where there are shops.
  const where = city || "Rwanda";

  useSeo({
    title: category ? `${category} in ${where} — book online | SHUWA` : city ? `Salons, barbers and more in ${city} — book online | SHUWA` : q ? `${q} — search | SHUWA` : pageSeo("/search").title,
    description: category
      ? `Compare ${category.toLowerCase()} in ${where}: prices, opening hours and verified reviews. Book online on SHUWA in seconds.`
      : city
      ? `Find salons, barbers, spas and clinics in ${city}. Compare prices and reviews and book online on SHUWA.`
      : pageSeo("/search").description,
    canonical: "/search",
    noindex: Boolean(q || category || city),
  });

  return (
    <div className="bg-slate-50">
      {/* Page header */}
      <div className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <h1 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
            {title} {!loading && <span className="text-muted-foreground">({total})</span>}
          </h1>
          <div className="mt-4 max-w-md">
            <Input
              defaultValue={q}
              placeholder="Search services or businesses"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const next = new URLSearchParams(params);
                  next.set("q", e.currentTarget.value);
                  setParams(next);
                }
              }}
            />
          </div>
          {date && (
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("date");
                setParams(next);
              }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
            >
              Open on {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:flex">
        {/* Sidebar filters */}
        <aside className="mb-6 w-full shrink-0 lg:mb-0 lg:w-64">
          <div className="sticky top-20 space-y-5">
            <div className="flex items-center gap-2 font-montserrat text-sm font-semibold text-slate-900">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </div>

            <Card className="p-4">
              <p className="mb-2 text-sm font-semibold text-slate-900">Sort by</p>
              <div className="space-y-1.5">
                {SORT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
                  >
                    <input
                      type="radio"
                      name="sort"
                      checked={sort === opt.value}
                      onChange={() => setSort(opt.value)}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              <p className="mb-2 text-sm font-semibold text-slate-900">Rating</p>
              <div className="space-y-1.5">
                {[0, 3, 4, 4.5].map((r) => (
                  <label
                    key={r}
                    className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
                  >
                    <input
                      type="radio"
                      name="rating"
                      checked={minRating === r}
                      onChange={() => setMinRating(r)}
                      className="h-3.5 w-3.5 accent-primary"
                    />
                    {r === 0 ? "Any rating" : `${r}+ stars`}
                  </label>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              <label className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                Promoted only
                <input
                  type="checkbox"
                  checked={promotedOnly}
                  onChange={(e) => setPromotedOnly(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
              </label>
            </Card>
          </div>
        </aside>

        {/* Results list */}
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {loading ? "Loading…" : `${filtered.length} of ${total} results`}
            </p>
            <div className="relative hidden sm:block">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none rounded-full border border-border bg-white py-2 pl-4 pr-9 text-sm font-medium text-slate-700 outline-none"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <ListingCardSkeleton key={i} />
              ))}
            </div>
          ) : filtered.length ? (
            <div className="space-y-4">
              {filtered.map((b) => (
                <ListingCard key={b.id} business={b} searchTerm={q} />
              ))}
            </div>
          ) : (
            <Card className="p-10 text-center text-muted-foreground">
              No businesses match your search yet.
            </Card>
          )}

          {/* Most popular treatments */}
          <div className="mt-10">
            <h2 className="mb-3 font-montserrat text-lg font-semibold text-slate-900">
              Most popular treatments
            </h2>
            <div className="flex flex-wrap gap-2">
              {POPULAR_TREATMENTS.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="rounded-full border border-border bg-white px-3.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-primary hover:text-primary"
                >
                  {t}
                </Link>
              ))}
            </div>
          </div>

          {/* SEO / FAQ block */}
          <div className="mt-10 space-y-4 border-t border-border pt-8 text-sm text-muted-foreground">
            <h2 className="font-montserrat text-lg font-semibold text-slate-900">
              Frequently asked questions
            </h2>
            <div>
              <p className="font-medium text-slate-900">
                How do I book an appointment{city ? ` in ${city}` : ""}?
              </p>
              <p>
                Browse the list above, open a business profile, and pick a service, staff member,
                and time that works for you — booking takes less than a minute.
              </p>
            </div>
            <div>
              <p className="font-medium text-slate-900">Can I see reviews before booking?</p>
              <p>
                Yes — every listing shows its average rating and number of reviews so you can
                compare businesses before you book.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
